import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import {
  Scales, ArrowUpRight, MapPin, CurrencyDollar, FileText, Buildings, CheckCircle
} from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { toast } from "sonner";

export default function LLCAnchor() {
  const [states, setStates] = useState([]);
  const [partners, setPartners] = useState([]);
  const [selectedCode, setSelectedCode] = useState("");
  const [detail, setDetail] = useState(null);
  const [showAgreement, setShowAgreement] = useState(false);

  useEffect(() => {
    api.get("/tools/llc/states").then((r) => {
      setStates(r.data.states);
      setPartners(r.data.partners);
    });
  }, []);

  useEffect(() => {
    if (!selectedCode) { setDetail(null); return; }
    api.get(`/tools/llc/state/${selectedCode}`).then((r) => setDetail(r.data));
  }, [selectedCode]);

  const downloadAgreement = () => {
    if (!detail?.operating_agreement_template) return;
    const blob = new Blob([detail.operating_agreement_template], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `EIGOO_Operating_Agreement_${detail.state.code}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Operating Agreement template downloaded.");
  };

  return (
    <div className="surface-card p-6 md:p-8" data-testid="llc-anchor">
      <div className="flex items-start gap-4 mb-6">
        <div className="w-12 h-12 border border-[#FFD700]/40 flex items-center justify-center flex-shrink-0">
          <Scales size={22} weight="duotone" className="text-[#FFD700]" />
        </div>
        <div>
          <div className="overline mb-1">// LLC Anchor</div>
          <h3 className="text-xl font-bold text-white">Legal Entity Module</h3>
          <p className="text-sm text-white/60 font-light mt-1">
            Director's voice: "Lock your legal hull before you scale. Pick your state — I'll walk you through it."
          </p>
        </div>
      </div>

      {/* State picker */}
      <div className="mb-6">
        <label className="overline text-[0.6rem] mb-2 block">Formation State</label>
        <Select value={selectedCode} onValueChange={setSelectedCode}>
          <SelectTrigger
            data-testid="llc-state-select"
            className="bg-[#0c0c0c] border-white/10 rounded-none h-12 text-white"
          >
            <SelectValue placeholder="Select a state to commission..." />
          </SelectTrigger>
          <SelectContent className="bg-[#0c0c0c] border-white/10 text-white max-h-72">
            {states.map((s) => (
              <SelectItem key={s.code} value={s.code} data-testid={`llc-state-option-${s.code}`}>
                {s.name} ({s.code}) — ${s.filing_fee}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <AnimatePresence mode="wait">
        {detail && (
          <motion.div
            key={detail.state.code}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="space-y-5"
            data-testid="llc-state-detail"
          >
            <div className="grid grid-cols-2 gap-3">
              <div className="border border-white/5 p-4">
                <div className="overline text-[0.55rem] mb-1">Filing Fee</div>
                <div className="font-mono text-xl text-[#FFD700]">${detail.state.filing_fee}</div>
              </div>
              <div className="border border-white/5 p-4">
                <div className="overline text-[0.55rem] mb-1">Annual Fee</div>
                <div className="font-mono text-xl text-[#FFD700]">${detail.state.annual_fee}</div>
              </div>
            </div>

            <div className="border-l-2 border-[#FFD700]/40 pl-4">
              <div className="overline text-[0.55rem] mb-1">Strategic Note</div>
              <p className="text-sm text-white/80 font-light">{detail.state.highlights}</p>
            </div>

            <a
              href={detail.state.gov_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="llc-gov-link"
              className="flex items-center justify-between p-4 border border-white/10 hover:border-[#FFD700]/40 transition-colors group"
            >
              <div className="flex items-center gap-3">
                <Buildings size={20} weight="duotone" className="text-[#FFD700]" />
                <div>
                  <div className="text-sm font-semibold text-white">Official {detail.state.name} Filing Portal</div>
                  <div className="text-[0.65rem] font-mono uppercase tracking-widest text-white/40">File directly with state</div>
                </div>
              </div>
              <ArrowUpRight size={18} className="text-white/40 group-hover:text-[#FFD700]" />
            </a>

            <div>
              <div className="overline text-[0.55rem] mb-3">// Filing Services (Done-For-You)</div>
              <div className="space-y-2">
                {partners.map((p) => (
                  <a
                    key={p.name}
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`llc-partner-${p.name.replace(/\s+/g, "-").toLowerCase()}`}
                    className="flex items-center justify-between p-3 border border-white/5 hover:border-[#FFD700]/30 transition-colors group"
                  >
                    <div>
                      <div className="text-sm font-semibold text-white">{p.name}</div>
                      <div className="text-xs text-white/50 font-light">{p.tagline}</div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="font-mono text-xs text-[#FFD700]">
                        {p.price_from === 0 ? "FREE" : `$${p.price_from}+`}
                      </span>
                      <ArrowUpRight size={14} className="text-white/40 group-hover:text-[#FFD700]" />
                    </div>
                  </a>
                ))}
              </div>
            </div>

            <button
              onClick={downloadAgreement}
              data-testid="llc-download-agreement"
              className="w-full btn-ghost py-3 text-xs flex items-center justify-center gap-2"
            >
              <FileText size={16} />
              Download Sovereign Operating Agreement Template
            </button>
          </motion.div>
        )}

        {!detail && (
          <motion.div className="text-center py-8 text-white/40 text-sm font-light">
            <MapPin size={28} weight="duotone" className="mx-auto mb-3 text-white/20" />
            Select your state to receive armored filing instructions.
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
